import { Divider, Grid, Typography, useTheme, Box, Grid2 } from '@mui/material';
import React, { FC, useState } from 'react';
import Drawer, { DrawerProps } from '@mui/material/Drawer';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Button } from '../../../Form/Button/button.scomponent';
import { Switch } from '../../../Form/Switch/switch.component';
import { DatePicker } from '../../../Form/DatePicker/date-picker.component';
import { IOrdersListPayload, OrderProductionStatusEnum, OrderStatusEnum } from '../../../../Services/api/orders/orders.interface';
import { TCategoryStore } from '../../../../Services/api/categories/categories.interface';

const SignInSchemaValidation = Yup.object().shape({
  name: Yup.string().required('Nome é obrigatório'),
  code: Yup.string(),
  ncm: Yup.string(),
  personalized: Yup.boolean(),
  createOnNuvemShop: Yup.boolean(),
  createOnOmie: Yup.boolean(),
  price: Yup.string().required('Preço Unitário é obrigatório'),
  description: Yup.string(),
  createOnb2b: Yup.boolean(),
  createOnb2c: Yup.boolean(),
});

interface IDrawerComponent extends DrawerProps {
  onToggle: (state: boolean) => void;
  title: string;
  subTitle?: string;
  search?: boolean;
  searchFunction?: () => void;
  titleStyle?: object;
  subTitleStyle?: object;
  open: boolean;
  handleSelect: (payload: IOrdersListPayload) => void;
}

const OrderFiltersDrawer: FC<IDrawerComponent> = ({
  onToggle,
  title,
  subTitle,
  open,
  titleStyle = {},
  subTitleStyle = {},
  handleSelect,
}) => {
  const theme = useTheme();
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>(['B2B', 'B2C']);
  const { control, watch, getValues } = useForm();

  const handleStatusChange = (status: string) => (checked: boolean) => {
    setSelectedStatuses(prev => {
      if (checked) {
        return [...prev, status];
      }
      return prev.filter(s => s !== status);
    });
  };

  const handleStoreChange = (store: string) => (checked: boolean) => {
    setSelectedStores(prev => {
      if (checked) {
        return [...prev, store];
      }
      return prev.filter(s => s !== store);
    });
  };

  const handleFilterSubmit = () => {
    const formValues = getValues();
    // Se os 2 stores estiverem selecionados, enviar store como undefined
    const storeValue = selectedStores.length === 2 ? undefined : selectedStores[0] as TCategoryStore;
    handleSelect({ 
      status: selectedStatuses, 
      store: storeValue, 
      page: 1,
      startDate: formValues.startDate || undefined,
      endDate: formValues.endDate || undefined,
    });
    onToggle(false);
  };

  return (
    <Drawer
      open={open}
      onClose={onToggle}
      anchor="right"
      style={{ zIndex: 99999999, flex: 1 }}
    >
      <Grid2 container sx={{ minWidth: '540px', padding: '30px 30px' }}>
        {/* Conteúdo do Drawer */}
        <Grid2 size={12}>
          <Typography sx={{ fontSize: '2rem', fontWeight: 'bold', ...titleStyle }}>
            {title}
          </Typography>
          {subTitle && (
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: '400',
                color: theme.palette.text.secondary,
                ...subTitleStyle,
              }}
            >
              {subTitle}
            </Typography>
          )}
          <Divider sx={{ marginTop: '20px', marginBottom: '10px' }} />
          <Typography
            sx={{ fontSize: '1rem', fontWeight: 500, color: theme.palette.text.secondary }}
          >
            Status
          </Typography>
          {[
            { label: 'Processando', value: OrderStatusEnum.OPEN },
            { label: 'Aguardando Produção', value: OrderProductionStatusEnum.STAND_BY },
            { label: 'Em produção', value: OrderProductionStatusEnum.PRODUCTION },
            { label: 'Parcialmente Produzido', value: OrderProductionStatusEnum.HALF_FINISHED },
            { label: 'Produzido', value: OrderProductionStatusEnum.FINISHED },
            { label: 'Aguardando Faturamento', value: OrderStatusEnum.AWAITING_INVOICING },
            { label: 'Aguardando Faturamento no OMIE', value: OrderStatusEnum.INVOICING },
            { label: 'Processando faturamento', value: OrderStatusEnum.PROCESSING_INVOICE },
            { label: 'Com Problemas de Faturamento', value: OrderStatusEnum.INVOICE_PROBLEM },
            { label: 'Aguardando Separação', value: OrderStatusEnum.AWAITING_PICKING },
            { label: 'Em Separação', value: OrderStatusEnum.IN_PICKING },
            { label: 'Com Problemas', value: OrderStatusEnum.DEAD },
            { label: 'Faturado', value: OrderStatusEnum.INVOICED },
            
          ].map((item) => (
            <Grid2 size={12} sx={{ marginTop: '20px' }} key={item.value}>
              <Switch
                label={item.label}
                name={`switch-${item.value}`}
                control={control}
                defaultValue={selectedStatuses.includes(item.value)}
                onChangeCallback={handleStatusChange(item.value)}
              />
            </Grid2>
          ))}

          <Divider sx={{ marginTop: '30px', marginBottom: '10px' }} />
          <Typography
            sx={{ fontSize: '1rem', fontWeight: 500, color: theme.palette.text.secondary }}
          >
            Loja
          </Typography>
          {[
            { label: 'B2B', value: 'B2B' },
            { label: 'B2C', value: 'B2C' },
            
          ].map((item) => (
            <Grid2 size={12} sx={{ marginTop: '20px' }} key={item.value}>
              <Switch
                label={item.label}
                name={`switch-store-${item.value}`}
                control={control}
                defaultValue={selectedStores.includes(item.value)}
                onChangeCallback={handleStoreChange(item.value)}
              />
            </Grid2>
          ))}

          <Divider sx={{ marginTop: '30px', marginBottom: '10px' }} />
          <Typography
            sx={{ fontSize: '1rem', fontWeight: 500, color: theme.palette.text.secondary }}
          >
            Período
          </Typography>
          <Grid2 size={12} sx={{ marginTop: '20px' }}>
            <DatePicker
              name="startDate"
              control={control}
              label="Data Inicial"
              maxDate={watch('endDate') || undefined}
            />
          </Grid2>
          <Grid2 size={12} sx={{ marginTop: '20px', marginBottom: '30px' }}>
            <DatePicker
              name="endDate"
              control={control}
              label="Data Final"
              minDate={watch('startDate') || undefined}
            />
          </Grid2>

          {/* Botões */}
          <Grid2 container spacing={2} justifyContent="flex-end" sx={{ marginTop: '30px' }}>
            <Grid2>
              <Button variant="outlined" onClick={() => onToggle(false)} sx={{ width: '120px'}}>
                Cancelar
              </Button>
            </Grid2>
            <Grid2>
              <Button variant="contained" onClick={handleFilterSubmit} sx={{ width: '120px'}}>
                Filtrar
              </Button>
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>
    </Drawer>
  );
};

export { OrderFiltersDrawer };
